/**
 * Display helpers for adapting a 12-month-priced quote to the actual
 * coverage period. Month-to-month terms are priced over 12 months in
 * `QuoteService.calculateQuote` (the underwriting tables assume an
 * annual window), but the actual term length is 3 months — the renewal
 * cron charges the tenant once per 3-month term. To avoid storing two
 * variants of the same quote, the DB row is the 12-month total and
 * every display / charge surface divides by 4 right before use.
 *
 * Numeric inputs are accepted in number, string ("123.45"), or null
 * form because TypeORM decimal columns are surfaced as strings and the
 * QuoteResponse shape uses numbers. Outputs are always numbers, rounded
 * to 2 decimals per item — sum of displayed line items may be 1 cent
 * off from displayed total in edge cases.
 */

/** Coverage periods we can render a quote for. Fixed renders verbatim. */
export type RenewalTermKind = "fixed" | "month-to-month" | null | undefined;

/**
 * Divisor for a renewal term. 12-month quote ÷ 4 yields the 3-month
 * figure that the tenant actually owes for a month-to-month renewal.
 * `fixed` (and `null` / `undefined`) leave the figure alone.
 */
export function divisorForRenewalTerm(kind: RenewalTermKind): number {
  return kind === "month-to-month" ? 4 : 1;
}

/** Human label for the displayed period — used in UI subtitles / badges. */
export function periodLabelForRenewalTerm(kind: RenewalTermKind): string {
  return kind === "month-to-month" ? "3-month total" : "12-month total";
}

/**
 * Fields we divide. All monetary; `quote`, `tax`, `policyFee`/`documentFee`,
 * `stampingFee`, `lateFee`, `billingFee`, `companyPolicyFee`, `total`.
 * Pass whatever subset you have — missing fields stay missing in the result.
 */
export interface QuoteLikeInput {
  quote?: number | string | null;
  tax?: number | string | null;
  /** QuoteResponse shape (frontend). */
  policyFee?: number | string | null;
  /** Quote entity shape (DB). */
  documentFee?: number | string | null;
  stampingFee?: number | string | null;
  lateFee?: number | string | null;
  billingFee?: number | string | null;
  companyPolicyFee?: number | string | null;
  total?: number | string | null;
}

export interface QuoteLikeOutput {
  quote: number | null;
  tax: number | null;
  policyFee: number | null;
  documentFee: number | null;
  stampingFee: number | null;
  lateFee: number | null;
  billingFee: number | null;
  companyPolicyFee: number | null;
  total: number | null;
}

const FIELDS = [
  "quote",
  "tax",
  "policyFee",
  "documentFee",
  "stampingFee",
  "lateFee",
  "billingFee",
  "companyPolicyFee",
  "total",
] as const;

function toNumberOrNull(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Divide each line item by the divisor implied by `renewalTerm`.
 * Round-half-up to 2 decimals per item (independent rounding — sum may
 * differ from divided total by ≤1 cent). The unmodified shape for
 * `fixed` is returned untouched (no rounding, no number coercion of
 * fields we didn't see).
 */
export function divideQuoteForDisplay(
  input: QuoteLikeInput,
  renewalTerm: RenewalTermKind,
): QuoteLikeOutput {
  const divisor = divisorForRenewalTerm(renewalTerm);
  const out: QuoteLikeOutput = {
    quote: null,
    tax: null,
    policyFee: null,
    documentFee: null,
    stampingFee: null,
    lateFee: null,
    billingFee: null,
    companyPolicyFee: null,
    total: null,
  };
  for (const f of FIELDS) {
    const raw = toNumberOrNull(input[f]);
    if (raw == null) {
      out[f] = null;
      continue;
    }
    out[f] = divisor === 1 ? raw : round2(raw / divisor);
  }
  return out;
}

/**
 * The "what the tenant pays" number for a single charge cycle on this
 * term. For `month-to-month` returns 12mo total / 4 (rounded to cents);
 * for `fixed` returns the total as-is. Returns null if total is null.
 *
 * Used by RenewalPaymentService for the Stripe charge amount and by
 * email templates so tenants see what was actually charged.
 */
export function chargeAmountForRenewalTerm(
  total: number | string | null | undefined,
  renewalTerm: RenewalTermKind,
): number | null {
  const n = toNumberOrNull(total);
  if (n == null) return null;
  const divisor = divisorForRenewalTerm(renewalTerm);
  return divisor === 1 ? n : round2(n / divisor);
}
